import type {
  LngLat,
  Shape,
  ShapeImage,
} from "../types";
import type { PixelPoint } from "./snapping";

/**
 * Géométrie du placement d'une image (`Shape.image`) sur une forme — extrait de
 * `MapLibreAdapter.ts` pour être testable unitairement (pas de dépendance MapLibre/DOM ici, que des
 * points/nombres), voir `imageQuad.test.ts`. Cette logique a régressé plusieurs fois (étirement,
 * cisaillement, débordement) avant d'être isolée ici — voir CLAUDE.md.
 *
 * Contient aussi quelques primitives génériques de géométrie écran (`rotatePointPx`, `boundsOf`,
 * `midpointPx`) réutilisées ailleurs dans `MapLibreAdapter.ts` (sélection multiple, poignées de
 * rotation) — pas seulement par le placement d'image.
 */

export type ImageQuad = [LngLat, LngLat, LngLat, LngLat];

/** Bounding box axis-aligned (lng/lat) d'un ensemble de points. */
export function boundsOf(points: LngLat[]): { minLng: number; minLat: number; maxLng: number; maxLat: number } {
  const lngs = points.map(([lng]) => lng);
  const lats = points.map(([, lat]) => lat);
  return {
    minLng: Math.min(...lngs),
    maxLng: Math.max(...lngs),
    minLat: Math.min(...lats),
    maxLat: Math.max(...lats),
  };
}

/** Nombre de sommets du polygone régulier approximant un cercle — voir `circleRingLngLat`. */
const CIRCLE_RING_SEGMENTS = 64;

/**
 * Anneau fermé (nombreux points) approximant le cercle défini par `[centre, point de bord]` (voir
 * `Shape.coordinates` pour `shapeKind: "circle"` : PAS un anneau fermé, juste ces 2 points bruts) —
 * calculé en lng/lat directement, sans dépendance à une carte réelle (approximation plate
 * mètres/degré, `cos(latitude)` pour compenser la compression de longitude — même principe que
 * `MapLibreAdapter.shapeBoundingBoxPx`, suffisant à l'échelle d'une forme individuelle). Sert
 * uniquement à produire une silhouette pour le rendu/la géométrie (`shapesToGeoJSON`,
 * `highlightShapesToGeoJSON`, `axisAlignedBboxQuad`, `imageQuadForClippedToShape`) — jamais
 * persistée sur `Shape.coordinates` elle-même, qui ne garde que les 2 points sources.
 */
export function circleRingLngLat([center, edge]: [LngLat, LngLat]): LngLat[] {
  const [centerLng, centerLat] = center;
  const [edgeLng, edgeLat] = edge;
  const metersPerDegLat = 111_320;
  const metersPerDegLng = 111_320 * Math.cos((centerLat * Math.PI) / 180);
  const radiusMeters = Math.hypot((edgeLng - centerLng) * metersPerDegLng, (edgeLat - centerLat) * metersPerDegLat);

  const ring: LngLat[] = [];
  for (let i = 0; i <= CIRCLE_RING_SEGMENTS; i++) {
    const angle = (2 * Math.PI * i) / CIRCLE_RING_SEGMENTS;
    ring.push([
      centerLng + (radiusMeters * Math.cos(angle)) / metersPerDegLng,
      centerLat + (radiusMeters * Math.sin(angle)) / metersPerDegLat,
    ]);
  }
  return ring;
}

/** Bounding box axis-aligned (lng/lat) d'une forme, sous forme de quad (coin de départ puis sens
 * horaire) — toujours calculée à partir des coordonnées, quel que soit le nombre de sommets (un
 * cercle est d'abord développé en anneau via `circleRingLngLat`, `shape.coordinates` ne portant que
 * ses 2 points sources). Voir `shapeImageQuad`/`containReferenceQuadPx`. */
export function axisAlignedBboxQuad(shape: Shape): ImageQuad {
  const uniquePoints =
    shape.shapeKind === "circle" ? circleRingLngLat(shape.coordinates as [LngLat, LngLat]) : shape.coordinates.slice(0, -1);
  const { minLng, maxLng, minLat, maxLat } = boundsOf(uniquePoints);
  return [
    [minLng, maxLat],
    [maxLng, maxLat],
    [maxLng, minLat],
    [minLng, minLat],
  ];
}

/**
 * 4 coins (lng/lat) sur lesquels plaquer l'image — coin de départ puis sens horaire. Pour un
 * quadrilatère (cas des rectangles/formes à 4 sommets du jeu de données), on réutilise directement
 * ses sommets, ce qui respecte aussi sa rotation. Pour un cercle, ou un polygone à main levée, on
 * retombe sur sa bounding box axis-aligned (voir `axisAlignedBboxQuad`).
 */
export function shapeImageQuad(shape: Shape): ImageQuad {
  if (shape.shapeKind === "circle") return axisAlignedBboxQuad(shape);
  const uniquePoints = shape.coordinates.slice(0, -1);
  if (uniquePoints.length === 4) return uniquePoints as ImageQuad;
  return axisAlignedBboxQuad(shape);
}

/**
 * Vrai si `quad` (4 points ÉCRAN, dans l'ordre haut-gauche/haut-droite/bas-droite/bas-gauche) est un
 * vrai rectangle (angles droits), pivoté ou non — pas un quadrilatère arbitraire (trapèze, etc.).
 * Sert à savoir si les 4 sommets propres d'une forme sont une référence sûre pour le mode "contain"
 * (`containQuad` suppose un parallélogramme : lui donner un quadrilatère qui n'en est pas un
 * cisaille l'image) — voir `containReferenceQuadPx`, qui retombe sur la bounding box sinon.
 * Tolérance RELATIVE (pas absolue) : reste valide à n'importe quelle échelle/zoom.
 */
export function isRectanglePx(quad: [PixelPoint, PixelPoint, PixelPoint, PixelPoint]): boolean {
  const [p0, p1, p2, p3] = quad;
  const u = { x: p1.x - p0.x, y: p1.y - p0.y };
  const v = { x: p3.x - p0.x, y: p3.y - p0.y };
  const uLen = Math.hypot(u.x, u.y);
  const vLen = Math.hypot(v.x, v.y);
  if (!uLen || !vLen) return false;

  const RELATIVE_TOLERANCE = 0.01;
  const cosAngle = (u.x * v.x + u.y * v.y) / (uLen * vLen);
  if (Math.abs(cosAngle) > RELATIVE_TOLERANCE) return false; // u/v pas perpendiculaires

  // Un parallélogramme a p2 = p0 + u + v ; un rectangle est un parallélogramme à angles droits
  // (déjà vérifié ci-dessus) — reste à vérifier que p2 correspond bien à cette prédiction.
  const expectedP2 = { x: p0.x + u.x + v.x, y: p0.y + u.y + v.y };
  const diag = Math.hypot(uLen, vLen);
  return Math.hypot(p2.x - expectedP2.x, p2.y - expectedP2.y) < diag * RELATIVE_TOLERANCE;
}

/**
 * Référence "sûre" pour le mode contain (voir `containQuad`) : les 4 sommets propres de la forme
 * s'ils forment un vrai rectangle (pivoté ou non — voir `isRectanglePx`), pour un ajustement exact
 * sans gaspillage ; sinon (quadrilatère quelconque type trapèze, ou polygone à main levée déjà
 * réduit à sa bbox par `shapeImageQuad`) la bounding box axis-aligned, qui EST toujours un vrai
 * rectangle — jamais les sommets bruts d'un quadrilatère non-rectangulaire, qui cisailleraient
 * l'image plaquée dessus.
 */
export function containReferenceQuadPx(
  shape: Shape,
  project: (point: LngLat) => PixelPoint,
): [PixelPoint, PixelPoint, PixelPoint, PixelPoint] {
  const [r0, r1, r2, r3] = shapeImageQuad(shape);
  const rawQuadPx: [PixelPoint, PixelPoint, PixelPoint, PixelPoint] = [
    project(r0),
    project(r1),
    project(r2),
    project(r3),
  ];
  if (isRectanglePx(rawQuadPx)) return rawQuadPx;

  const [b0, b1, b2, b3] = axisAlignedBboxQuad(shape);
  return [project(b0), project(b1), project(b2), project(b3)];
}

/**
 * `image.width`/`.height` s'ils sont exploitables (nombres finis strictement positifs), sinon
 * `undefined`. Utilisé par `MapLibreAdapter.imageQuadFor` pour éviter le sondage asynchrone des
 * dimensions réelles (`probeImageDimensions`, un `Image()` DOM + un rendu complet une fois résolu)
 * quand la donnée source les fournit déjà : à l'échelle de nombreuses images distinctes, ce sondage
 * déclenche potentiellement autant de rendus complets en cascade, un coût que ces champs permettent
 * d'éviter entièrement quand ils sont fiables. Validité minimale seulement (`> 0`, fini) — PAS une
 * vérification que le ratio est correct (rien ne garantit qu'un consommateur les renseigne avec le
 * bon ratio) : un compromis assumé pour la performance, pas une nouvelle promesse de fiabilité totale.
 */
export function trustedImageDimensions(image: ShapeImage | undefined): { width: number; height: number } | undefined {
  const { width, height } = image ?? {};
  if (!Number.isFinite(width) || !Number.isFinite(height) || width! <= 0 || height! <= 0) return undefined;
  return { width: width!, height: height! };
}

/**
 * Matrice CSS `matrix3d` d'une vraie transformation projective (homographie) amenant le
 * rectangle (0,0)-(width,height) exactement sur le quadrilatère écran `quad` (dans l'ordre
 * haut-gauche/haut-droite/bas-droite/bas-gauche). Contrairement à une transform 2D affine
 * (translation/rotation/échelle), une homographie respecte aussi la perspective — nécessaire
 * puisque MapLibre en pitch (inclinaison 3D) projette les 4 coins d'une forme avec une vraie
 * perspective, plus seulement une rotation à plat. Algorithme : Heckbert, "Fundamentals of
 * Texture Mapping and Image Warping" (1989), méthode classique de mapping carré unité → quad.
 */
export function quadMatrix3d(
  quad: [PixelPoint, PixelPoint, PixelPoint, PixelPoint],
  width: number,
  height: number,
): string | null {
  const [p0, p1, p2, p3] = quad;

  const dx1 = p1.x - p2.x;
  const dy1 = p1.y - p2.y;
  const dx2 = p3.x - p2.x;
  const dy2 = p3.y - p2.y;
  const dx3 = p0.x - p1.x + p2.x - p3.x;
  const dy3 = p0.y - p1.y + p2.y - p3.y;

  let g = 0;
  let h = 0;
  if (dx3 !== 0 || dy3 !== 0) {
    const denom = dx1 * dy2 - dx2 * dy1;
    g = (dx3 * dy2 - dx2 * dy3) / denom;
    h = (dx1 * dy3 - dx3 * dy1) / denom;
  }

  const a = p1.x - p0.x + g * p1.x;
  const b = p3.x - p0.x + h * p3.x;
  const c = p0.x;
  const d = p1.y - p0.y + g * p1.y;
  const e = p3.y - p0.y + h * p3.y;
  const f = p0.y;

  // Le rectangle source est (width, height), pas le carré unité : on absorbe la mise à
  // l'échelle dans la matrice plutôt que d'ajouter un transform CSS séparé.
  const m = [
    a / width, d / width, 0, g / width,
    b / height, e / height, 0, h / height,
    0, 0, 1, 0,
    c, f, 0, 1,
  ];
  if (!m.every(Number.isFinite)) return null; // quadrilatère dégénéré (aire nulle)
  return `matrix3d(${m.join(",")})`;
}

/**
 * Matrice CSS `matrix` (affine 2D, 3 points : coin haut-gauche/haut-droite/bas-gauche — le 4e est
 * implicite dans une transform affine) amenant (0,0)-(width,height) sur `quad`. Utilisée quand la
 * carte est à plat (pitch ≈ 0) : la rotation/mise à l'échelle suffit alors exactement (voir
 * `quadMatrix3d`), et `matrix()` reste nette à tout zoom — contrairement à `matrix3d()`, que les
 * navigateurs rasterisent sur un calque 3D à taille fixe puis remettent à l'échelle (flou).
 */
export function quadMatrix2d(
  quad: [PixelPoint, PixelPoint, PixelPoint, PixelPoint],
  width: number,
  height: number,
): string | null {
  const [p0, p1, , p3] = quad;
  const a = (p1.x - p0.x) / width;
  const b = (p1.y - p0.y) / width;
  const c = (p3.x - p0.x) / height;
  const d = (p3.y - p0.y) / height;
  if (![a, b, c, d, p0.x, p0.y].every(Number.isFinite)) return null; // quadrilatère dégénéré (aire nulle)
  return `matrix(${a}, ${b}, ${c}, ${d}, ${p0.x}, ${p0.y})`;
}

/**
 * Réduit `quad` (parallélogramme écran : coin haut-gauche/haut-droite/bas-droite/bas-gauche) à son
 * plus grand sous-rectangle de même orientation ayant le ratio largeur/hauteur de l'image source
 * (`imageWidth`/`imageHeight`) — l'équivalent de `object-fit: contain`. Sans ça, `quadMatrix2d`/
 * `quadMatrix3d` mappent le rectangle source exactement sur `quad` : si son ratio diffère de celui
 * de l'image (le cas courant — rien ne garantit qu'une forme ait le même ratio que l'image qu'on y
 * plaque), l'image est étirée de façon non uniforme.
 *
 * `horizontalAlign` (voir `ShapeImage.horizontalAlign`, défaut 0.5) glisse le sous-rectangle le long
 * de `u` (arête haut-gauche -> haut-droite, la "largeur" de la forme) dans la marge laissée par le
 * "contain" sur cet axe : 0 = collé au bord gauche (`p0`/`p3`), 1 = collé au bord droit (`p1`/`p2`),
 * 0.5 = centré (comportement historique). `verticalAlign` (voir `ShapeImage.verticalAlign`) fait de
 * même le long de `v` (arête haut-gauche -> bas-gauche, la "hauteur" de la forme) : 0 = collé en
 * haut (`p0`/`p1`), 1 = collé en bas (`p3`/le coin bas-droite), 0.5 = centré. Chaque axe est sans
 * effet s'il contraint déjà le "contain" (`widthFraction`/`heightFraction === 1`, aucune marge à cet
 * endroit) — ils sont par ailleurs totalement indépendants l'un de l'autre.
 *
 * `sizeScale` (voir `ShapeImage.scale`, défaut 1) RÉDUIT le sous-rectangle EN PLUS du "contain" —
 * DOIT être appliqué ICI (avant de calculer les marges `1 - widthFraction`/`1 - heightFraction`
 * utilisées par `horizontalAlign`/`verticalAlign`), pas après coup en rétrécissant le quad déjà
 * positionné autour de son propre centre (ce que fait `scaleAndRotateQuadPx`, réservée depuis à la
 * seule rotation — voir `MapLibreAdapter.imageQuadFor`/`applyImageOverlayTransform`) : rétrécir
 * après coup laisserait un vide entre l'image et le bord malgré `horizontalAlign: 0`/`1` (l'image
 * reculerait vers SON PROPRE centre, pas vers le bord visé) dès que `scale < 1`. En l'intégrant ici,
 * `widthFraction`/`heightFraction` reflètent la taille RÉELLEMENT rendue, donc `horizontalAlign: 0`
 * colle bien l'image, quelle que soit sa taille, contre le bord gauche du quad — jamais à mi-chemin.
 */
export function containQuad(
  quad: [PixelPoint, PixelPoint, PixelPoint, PixelPoint],
  imageWidth: number,
  imageHeight: number,
  horizontalAlign = 0.5,
  verticalAlign = 0.5,
  sizeScale = 1,
): [PixelPoint, PixelPoint, PixelPoint, PixelPoint] {
  const [p0, p1, , p3] = quad;
  const u = { x: p1.x - p0.x, y: p1.y - p0.y }; // arête haut-gauche -> haut-droite ("largeur" de la forme)
  const v = { x: p3.x - p0.x, y: p3.y - p0.y }; // arête haut-gauche -> bas-gauche ("hauteur" de la forme)
  const quadWidth = Math.hypot(u.x, u.y);
  const quadHeight = Math.hypot(v.x, v.y);
  if (!quadWidth || !quadHeight || !imageWidth || !imageHeight) return quad;

  // Facteur d'échelle (contain, réduit en plus par `sizeScale`) : celui qui fait toucher un des deux
  // côtés sans dépasser l'autre, à `sizeScale` près.
  const scale = Math.min(quadWidth / imageWidth, quadHeight / imageHeight) * sizeScale;
  const widthFraction = (scale * imageWidth) / quadWidth;
  const heightFraction = (scale * imageHeight) / quadHeight;

  // Fraction (0..1) le long de `u`/`v`, de `p0` vers `p1`/`p3`, du CENTRE du sous-rectangle : 0.5 -/+
  // la moitié de la marge disponible sur chaque axe, pondérée par `horizontalAlign`/`verticalAlign`.
  const centerUFraction = widthFraction / 2 + horizontalAlign * (1 - widthFraction);
  const centerVFraction = heightFraction / 2 + verticalAlign * (1 - heightFraction);
  const center = {
    x: p0.x + centerUFraction * u.x + centerVFraction * v.x,
    y: p0.y + centerUFraction * u.y + centerVFraction * v.y,
  };
  const halfU = { x: (u.x * widthFraction) / 2, y: (u.y * widthFraction) / 2 };
  const halfV = { x: (v.x * heightFraction) / 2, y: (v.y * heightFraction) / 2 };

  return [
    { x: center.x - halfU.x - halfV.x, y: center.y - halfU.y - halfV.y },
    { x: center.x + halfU.x - halfV.x, y: center.y + halfU.y - halfV.y },
    { x: center.x + halfU.x + halfV.x, y: center.y + halfU.y + halfV.y },
    { x: center.x - halfU.x + halfV.x, y: center.y - halfU.y + halfV.y },
  ];
}

/** Fait pivoter `p` de `deg` autour de `center`, en pixels écran — même formule que la rotation de
 * forme (voir `MapLibreAdapter.handleRotateDragMove`), réutilisée aussi pour le rectangle de
 * sélection (BESOINS.md §13) et pour `scaleAndRotateQuadPx`. */
export function rotatePointPx(p: PixelPoint, center: PixelPoint, deg: number): PixelPoint {
  const rad = (deg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const dx = p.x - center.x;
  const dy = p.y - center.y;
  return { x: center.x + dx * cos - dy * sin, y: center.y + dx * sin + dy * cos };
}

/**
 * Réduit `quad` (parallélogramme écran quelconque, pas forcément un rectangle axis-aligned) autour
 * de son propre centre par `scale`, puis le fait pivoter de `rotationDeg` degrés supplémentaires
 * autour de ce même centre — utilisé pour `Shape.image.rotation` (voir `MapLibreAdapter.imageQuadFor`
 * /`applyImageOverlayTransform`, toujours appelée avec `scale: 1` : `Shape.image.scale` est
 * désormais appliqué DANS `containQuad`, pas ici, voir son commentaire — sinon `horizontalAlign`/
 * `verticalAlign` laisseraient un vide entre l'image réduite et le bord visé, l'image reculant vers
 * SON PROPRE centre plutôt que de rester collée au bord). `scale` reste un paramètre générique de
 * cette fonction (utile indépendamment de `containQuad`, voir ses propres tests), appliqué APRÈS
 * `containQuad` : une étape purement géométrique de plus, indépendante de l'aspect ratio ou de
 * l'orientation d'origine.
 */
export function scaleAndRotateQuadPx(
  quad: [PixelPoint, PixelPoint, PixelPoint, PixelPoint],
  scale: number,
  rotationDeg: number,
): [PixelPoint, PixelPoint, PixelPoint, PixelPoint] {
  const center: PixelPoint = {
    x: (quad[0].x + quad[1].x + quad[2].x + quad[3].x) / 4,
    y: (quad[0].y + quad[1].y + quad[2].y + quad[3].y) / 4,
  };
  const scaled = quad.map((p) => ({
    x: center.x + (p.x - center.x) * scale,
    y: center.y + (p.y - center.y) * scale,
  })) as [PixelPoint, PixelPoint, PixelPoint, PixelPoint];

  if (!rotationDeg) return scaled;
  return scaled.map((p) => rotatePointPx(p, center, rotationDeg)) as [PixelPoint, PixelPoint, PixelPoint, PixelPoint];
}

/**
 * Rotation (degrés) à appliquer à un quad image (voir `scaleAndRotateQuadPx`), compte tenu de
 * `image.rotateWithMap` : par défaut (absent/faux), l'image pivote avec la carte — rien à ajouter,
 * le quad projeté depuis des coins en lng/lat porte déjà cette rotation. Si vrai, l'image doit au
 * contraire rester fixe à l'écran (même esprit que le texte, voir `ShapeImage.rotateWithMap`) : le
 * quad projeté porte alors une rotation "cachée" de `-bearingDeg` (dans la convention horaire de
 * `rotatePointPx`, que l'on retrouve en observant qu'une carte tournée de +90° de bearing fait
 * apparaître un point fixe pivoté anti-horaire de 90° à l'écran) qu'il faut annuler en ajoutant
 * `+bearingDeg`.
 */
export function effectiveImageRotationDeg(image: ShapeImage | undefined, bearingDeg: number, defaultRotationDeg = 0): number {
  // `defaultRotationDeg` : sans objet pour une image qui reste à l'endroit à l'écran.
  const own = image?.rotation ?? (image?.rotateWithMap ? 0 : defaultRotationDeg);
  return own + (image?.rotateWithMap ? bearingDeg : 0);
}

/** Vrai si l'image d'une forme est plaquée sur un cadre aligné sur le nord (cercle, polygone libre)
 * plutôt que sur ses propres côtés (quadrilatère) — voir `shapeImageQuad`. */
export function isNorthAlignedImageQuad(shape: Shape): boolean {
  return shape.shapeKind === "circle" || shape.coordinates.slice(0, -1).length !== 4;
}

/** Rotation d'une image qui n'en fixe pas : le cap de la vue de départ sur un cadre aligné au nord, 0 sinon. */
export function defaultImageRotationDeg(shape: Shape, startRotationDeg: number): number {
  return isNorthAlignedImageQuad(shape) ? startRotationDeg : 0;
}

/**
 * Rotation (degrés) propre à la FORME elle-même à ajouter à celle de l'image (voir
 * `effectiveImageRotationDeg`, qui ne couvre que `ShapeImage.rotation`/`.rotateWithMap`) —
 * nécessaire seulement pour un cercle : ses `coordinates` (`[centre, point de bord]`) ne portent
 * aucune orientation (un cercle est invariant par rotation autour de son centre — voir
 * `circleRingLngLat`, qui ne retient que la distance centre/bord, jamais son angle), contrairement à
 * un rectangle/polygone dont les sommets pivotent physiquement (`RotateShapeCommand`/
 * `MapLibreAdapter.handleRotateDragMove`) — `shapeImageQuad` en hérite alors directement de cette
 * rotation, sans jamais avoir besoin de lire `Shape.rotation`. Pour un cercle, c'est au contraire le
 * SEUL endroit où `Shape.rotation` a un effet visuel sur l'image plaquée dessus — sans quoi elle
 * reste figée, quelle que soit la poignée de rotation utilisée. Exprimée en pixels écran, même
 * convention que `handleRotateDragMove`/`rotatePointPx` (qui alimentent `Shape.rotation`), pour
 * rester dans le même repère que `scaleAndRotateQuadPx`, où cette valeur est ajoutée.
 *
 * `bearingDeg` (le bearing COURANT, pas celui capturé à la création) y est ajouté pour un cercle,
 * même raison/même signe que `rotateWithMap` ci-dessus : `shapeImageQuad` d'un cercle vient toujours
 * de `axisAlignedBboxQuad`, un carré fixe en lng/lat aligné sur le nord géographique — jamais sur le
 * bearing courant de la vue, contrairement à un rectangle, dont les sommets (capturés via
 * `unproject` au clic) encodent déjà le bearing du moment où il a été dessiné. Sans cette
 * compensation, `Shape.rotation` à sa valeur par défaut (0, y compris pour un cercle CHARGÉ depuis
 * un JSON dont la vue par défaut a son propre `configurations.rotation` — voir BESOINS.md §8)
 * afficherait l'image alignée au nord géographique plutôt que "droite" à l'écran comme le serait
 * l'équivalent sur un rectangle. `+bearingDeg` (pas `-`) annule exactement le tilt écran "caché" de
 * `-bearingDeg` que porte ce carré fixe une fois projeté — suit dynamiquement le bearing courant
 * (pas seulement celui du moment où l'image a été ajoutée), donc reste correct même si la vue a
 * changé depuis. `Shape.rotation` s'additionne PAR-DESSUS cette compensation, comme un réglage
 * manuel indépendant (poignée de rotation) plutôt qu'une valeur absolue.
 */
export function shapeOwnImageRotationDeg(shape: Shape, _bearingDeg: number): number {
  // Pas de compensation du bearing courant : la rotation par défaut de l'image s'en charge.
  return shape.shapeKind === "circle" ? shape.rotation : 0;
}

/** Point médian de deux points écran. */
export function midpointPx(a: PixelPoint, b: PixelPoint): PixelPoint {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

/**
 * Vrai si `point` est à l'intérieur de `polygon` (algorithme classique du lancer de rayon —
 * fonctionne pour un polygone simple quelconque, convexe ou non).
 */
export function isPointInPolygonPx(point: PixelPoint, polygon: PixelPoint[]): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const pi = polygon[i]!;
    const pj = polygon[j]!;
    const crosses = pi.y > point.y !== pj.y > point.y;
    if (crosses && point.x < ((pj.x - pi.x) * (point.y - pi.y)) / (pj.y - pi.y) + pi.x) {
      inside = !inside;
    }
  }
  return inside;
}

/**
 * Vrai si `rect` (4 coins) tient entièrement dans `polygon` — teste les 4 coins et les 4 milieux
 * d'arête (approximation bon marché plutôt qu'une vraie intersection segment/segment, suffisante
 * pour les formes simples de cet outil ; un polygone très concave pourrait laisser passer un léger
 * débordement entre deux points testés).
 */
export function rectFitsInPolygonPx(rect: [PixelPoint, PixelPoint, PixelPoint, PixelPoint], polygon: PixelPoint[]): boolean {
  const testPoints = [
    ...rect,
    midpointPx(rect[0], rect[1]),
    midpointPx(rect[1], rect[2]),
    midpointPx(rect[2], rect[3]),
    midpointPx(rect[3], rect[0]),
  ];
  return testPoints.every((p) => isPointInPolygonPx(p, polygon));
}

/**
 * Réduit `rect` (centré, homothétie vers son propre centre) jusqu'à tenir entièrement dans
 * `polygon` — nécessaire pour les formes qui ne sont pas des quadrilatères (`shapeImageQuad` y
 * plaque alors l'image sur la boîte englobante, plus grande que la silhouette réelle : un triangle
 * avec une image carrée dépasserait sinon dans les coins), et pour un quadrilatère qui n'est pas un
 * vrai rectangle (`containReferenceQuadPx` y retombe aussi sur la bbox). Recherche dichotomique du
 * plus grand facteur d'échelle qui tient, plutôt qu'une formule géométrique dédiée à chaque forme de
 * polygone. No-op pour un vrai rectangle (le contain y tient déjà par construction).
 */
export function shrinkQuadToFitPolygonPx(
  rect: [PixelPoint, PixelPoint, PixelPoint, PixelPoint],
  polygon: PixelPoint[],
): [PixelPoint, PixelPoint, PixelPoint, PixelPoint] {
  if (rectFitsInPolygonPx(rect, polygon)) return rect;

  const center = {
    x: (rect[0].x + rect[1].x + rect[2].x + rect[3].x) / 4,
    y: (rect[0].y + rect[1].y + rect[2].y + rect[3].y) / 4,
  };
  const scaledTowardCenter = (factor: number): [PixelPoint, PixelPoint, PixelPoint, PixelPoint] =>
    rect.map((p) => ({
      x: center.x + (p.x - center.x) * factor,
      y: center.y + (p.y - center.y) * factor,
    })) as [PixelPoint, PixelPoint, PixelPoint, PixelPoint];

  let lo = 0; // toujours valide (rectangle réduit à un point, au centre)
  let hi = 1; // ne tient pas (sinon on serait déjà sorti ci-dessus)
  for (let i = 0; i < 20; i++) {
    const mid = (lo + hi) / 2;
    if (rectFitsInPolygonPx(scaledTowardCenter(mid), polygon)) {
      lo = mid;
    } else {
      hi = mid;
    }
  }
  return scaledTowardCenter(lo);
}

/**
 * Anneau écran (fermé, premier === dernier point) agrandi d'environ `marginPx` pixels vers
 * l'EXTÉRIEUR, par une homothétie centrée sur son centroïde — voir `MapLibreAdapter.
 * startHighlightAnimation`, qui l'utilise pour le halo de surbrillance pulsant.
 *
 * Une homothétie de rapport > 1 centrée sur le centroïde pousse TOUJOURS chaque point plus loin du
 * centroïde, donc toujours vers l'extérieur, par construction géométrique — contrairement à
 * `line-offset` (paint MapLibre), dont le signe dépend à la fois du sens de parcours de l'anneau ET
 * du sens de l'axe Y de la projection Web Mercator (inversé par rapport à la latitude), et qui a été
 * la source d'erreurs de sens à répétition avant ce choix (voir l'historique git de
 * `startHighlightAnimation`). Approximation assumée : la marge obtenue n'est exactement `marginPx`
 * que pour une forme proche d'un cercle (un rectangle très allongé aurait une marge un peu plus
 * grande sur son grand côté que sur son petit) — largement suffisant pour un halo de pulsation, pas
 * un outil de mesure précise.
 */
export function outwardExpandedRingPx(ringPx: PixelPoint[], marginPx: number): PixelPoint[] {
  const points = ringPx.slice(0, -1);
  const centroid = points.reduce(
    (acc, p) => ({ x: acc.x + p.x / points.length, y: acc.y + p.y / points.length }),
    { x: 0, y: 0 },
  );
  const avgRadiusPx = points.reduce((sum, p) => sum + Math.hypot(p.x - centroid.x, p.y - centroid.y), 0) / points.length;
  const scale = avgRadiusPx > 0 ? (avgRadiusPx + marginPx) / avgRadiusPx : 1;
  const expanded = points.map((p) => ({
    x: centroid.x + (p.x - centroid.x) * scale,
    y: centroid.y + (p.y - centroid.y) * scale,
  }));
  return [...expanded, expanded[0]!];
}
